/**
 * „NEW" таг на корица — една истина за админа, каталога и записа в базата.
 *
 * Периодът се пази като ДАТИ без час (`@db.Date`) и се сравнява като низове
 * `YYYY-MM-DD`. Това не е мързел, а нарочно:
 *
 *   • Тагът важи за цял ден, а не от някакъв час нататък — точно както го
 *     задава админът („от 10-и до 20-и").
 *   • Сравнението е детерминистично на сървъра и в браузъра. При час-точна
 *     логика SSR HTML-ът (често кеширан) и първият клиентски рендер могат да
 *     дадат различен резултат и React да гръмне с hydration mismatch. С
 *     дневна точност двете страни се разминават само ако рендерът улучи
 *     полунощ в София.
 *
 * Празно „от" = веднага; празно „до" = безсрочно. И двата края са включително.
 */

/** Часовата зона, в която работи магазинът — денят започва и свършва по нея. */
const SHOP_TIME_ZONE = 'Europe/Sofia';

/** Етикетът върху корицата. Латиница, както е брандирана и самата корица. */
export const COVER_NEW_BADGE_LABEL = 'NEW';

/** `sv-SE` е единственият локал, който форматира точно като YYYY-MM-DD. */
const isoDateFormatter = new Intl.DateTimeFormat('sv-SE', {
  timeZone: SHOP_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const ISO_DATE_LENGTH = 10;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Днешната дата по часовника на магазина.
 * @param {Date} [now]
 * @returns {string} YYYY-MM-DD
 */
export function coverNewToday(now = new Date()) {
  return isoDateFormatter.format(now);
}

/**
 * Каквото дойде (Date от Prisma, ISO низ от JSON, стойност от `<input type="date">`)
 * → `YYYY-MM-DD`, или `''` ако не е дата.
 *
 * Датите от `@db.Date` се връщат като полунощ в UTC, затова тук се четат в UTC —
 * с локални getter-и една дата в отрицателна зона би се върнала с ден назад.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function toCoverNewDate(value) {
  if (!value) {
    return '';
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, ISO_DATE_LENGTH);
  }
  if (typeof value === 'string') {
    const head = value.slice(0, ISO_DATE_LENGTH);
    return ISO_DATE_RE.test(head) ? head : '';
  }
  return '';
}

/**
 * Стойност за колоната в базата: полунощ UTC или `null`.
 * @param {unknown} value
 * @returns {Date | null}
 */
export function coverNewDateForDb(value) {
  const iso = toCoverNewDate(value);
  return iso ? new Date(`${iso}T00:00:00.000Z`) : null;
}

/**
 * Да се показва ли тагът за тази корица днес.
 *
 * @param {{ isNew?: boolean, newFrom?: unknown, newUntil?: unknown } | null | undefined} template
 * @param {string} [today] YYYY-MM-DD; подава се в тестовете
 * @returns {boolean}
 */
export function isCoverNew(template, today = coverNewToday()) {
  if (!template?.isNew) {
    return false;
  }
  const from = toCoverNewDate(template.newFrom);
  if (from && today < from) {
    return false;
  }
  const until = toCoverNewDate(template.newUntil);
  if (until && today > until) {
    return false;
  }
  return true;
}
